import type { DocTypeId, StructuredDocument } from '../types/document'

// "Nearest office": opens the phone's map app searching near the person.
// The location goes only to the map link the person taps — Samjho's server
// never sees it, and nothing is stored.

/** What to search for on the map, per document type (map search works best in English). */
const PLACES: Partial<Record<DocTypeId, string>> = {
  income_certificate: 'Tehsil office',
  caste_certificate: 'Tehsil office',
  domicile_certificate: 'Tehsil office',
  birth_certificate: 'Municipal corporation office',
  death_certificate: 'Municipal corporation office',
  electricity_bill: 'Electricity office',
  water_bill: 'Water supply office',
  ration_card: 'Food supply office',
  aadhaar: 'Aadhaar Seva Kendra',
  pan_card: 'PAN card centre',
  bank_notice: 'Bank branch',
  loan_notice: 'Bank branch',
  income_tax_notice: 'Aaykar Seva Kendra',
  pension: 'Post office',
  court_notice: 'District court',
  land_record: 'Tehsil office',
  property_tax: 'Municipal corporation office',
  scholarship: 'Common Service Centre',
  farmer_scheme: 'Krishi Vigyan Kendra',
  school_letter: 'Block education office',
  marksheet: 'Block education office',
  insurance: 'Insurance office',
  traffic_challan: 'RTO office',
  job_notice: 'Employment exchange',
  application_receipt: 'Common Service Centre',
  application_decision: 'Common Service Centre'
}

export interface PlaceSearch {
  /** Map search text. */
  query: string
  /** Shown on the button. */
  label: string
}

/** The office that sent the document first (it's usually where to go), then the kind of office for this document. */
export function placesFor(doc: StructuredDocument): PlaceSearch[] {
  const out: PlaceSearch[] = []
  const authority = doc.authority?.value.replace(/\s+/g, ' ').trim()
  if (authority && authority.length >= 4 && authority.length <= 80) out.push({ query: authority, label: authority })
  const place = PLACES[doc.documentType] ?? 'Common Service Centre'
  if (!out.some((o) => o.query.toLowerCase().includes(place.toLowerCase()))) out.push({ query: place, label: place })
  return out.slice(0, 2)
}

export function mapsUrl(query: string, position?: { latitude: number; longitude: number }): string {
  const q = encodeURIComponent(query)
  if (!position) return `https://www.google.com/maps/search/?api=1&query=${q}`
  const lat = position.latitude.toFixed(4)
  const lng = position.longitude.toFixed(4)
  return `https://www.google.com/maps/search/${q}/@${lat},${lng},14z`
}

/** Asks for the phone's location (the browser shows its own permission prompt); falls back to a plain map search. */
export function currentPosition(timeout = 8000): Promise<{ latitude: number; longitude: number } | undefined> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(undefined)
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(undefined),
      { enableHighAccuracy: false, timeout, maximumAge: 5 * 60_000 }
    )
  })
}

/** Document types where government schemes are worth looking up. */
export const SCHEME_TYPES: DocTypeId[] = [
  'income_certificate',
  'caste_certificate',
  'domicile_certificate',
  'ration_card',
  'farmer_scheme',
  'scholarship',
  'pension',
  'birth_certificate',
  'land_record'
]

/** Document types where free legal aid (Legal Services Authorities Act, 1987) may help. */
export const LEGAL_TYPES: DocTypeId[] = ['court_notice', 'loan_notice', 'land_record']

/** NALSA's national toll-free legal aid helpline (nalsa.gov.in). */
export const LEGAL_HELPLINE = '15100'
